import { CsvCell } from "@src/lib/utils/csv";
import { extractTextFromReactElement } from "@src/lib/utils/reactTextExtraction";
import React from "react";

export type RenderFns<T, K extends string> = Record<
  K,
  (item: T) => React.ReactNode
>;

export type ExportConfig<T, K extends string> = {
  title: string;
  exportColumns: () => readonly K[];
  exportColumnTitles?: Record<K, string>;
  exportFns: Record<K, (item: T) => CsvCell>;
};

export type EnhancedExportConfig<T, ColumnType extends string> = {
  title: string;
  allExportColumns: readonly ColumnType[];
  combinedColumnTitles: readonly CsvCell[];
  combinedExportFns: Record<ColumnType, (item: T) => CsvCell>;
};

/**
 * Enhances an export configuration by adding missing visible columns that aren't explicitly
 * configured. For missing columns, it uses their render functions and extracts text content
 * for export.
 *
 * @param exportConfig - The original export configuration
 * @param visibleColumns - Array of currently visible column identifiers
 * @param renderFns - Functions that render each column's content
 * @param columnTitles - Visible column titles from the table (used as CSV headers for auto-added columns)
 * @returns Enhanced export configuration with all visible columns included, using human-readable column titles
 */
export function addMissingVisibleColumnsToExportConfigUsingReactTextExtraction<
  T,
  ColumnType extends string,
  ExportColumnType extends string,
>(
  exportConfig: ExportConfig<T, ExportColumnType>,
  visibleColumns: readonly ColumnType[],
  renderFns: RenderFns<T, ColumnType>,
  columnTitles?: Record<ColumnType, string>,
): EnhancedExportConfig<T, ColumnType | ExportColumnType> {
  const configuredColumns = exportConfig.exportColumns();

  // Find columns that are visible but not in export config
  const missingColumns = visibleColumns.filter(
    (col) => !configuredColumns.includes(col as unknown as ExportColumnType),
  );

  // Create combined column list
  const allExportColumns = [...configuredColumns, ...missingColumns] as const;

  // Create export functions for missing columns using their render functions
  const autoGeneratedExportFns = Object.fromEntries(
    missingColumns.map((col) => [
      col,
      (item: T) => extractTextFromReactElement(renderFns[col](item)),
    ]),
  ) as Record<ColumnType, (item: T) => CsvCell>;

  const combinedExportFns = {
    ...exportConfig.exportFns,
    ...autoGeneratedExportFns,
  } as Record<ColumnType | ExportColumnType, (item: T) => CsvCell>;

  // Generate column titles - prefer user-visible titles over raw column names
  const combinedColumnTitles = allExportColumns.map((c) => {
    if (configuredColumns.includes(c as unknown as ExportColumnType)) {
      // For configured columns, prefer export config titles, then fall back to visible column titles, then raw column name
      return (
        exportConfig.exportColumnTitles?.[c as ExportColumnType] ||
        columnTitles?.[c as ColumnType] ||
        c
      );
    }
    // For auto-added columns, prefer visible column titles over raw column names
    return columnTitles?.[c as ColumnType] || c;
  });

  return {
    title: exportConfig.title,
    allExportColumns,
    combinedColumnTitles,
    combinedExportFns,
  };
}
